import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refresh(); setStatus("paid"); return; }
        if (data.status === "expired" || tries > 8) { setStatus("error"); return; }
      } catch { if (tries > 8) { setStatus("error"); return; } }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="pt-32 min-h-screen text-center px-6">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto">
        {status === "checking" && <p className="text-text-muted font-display text-3xl italic">Confirming your membership…</p>}
        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={48} className="mx-auto text-gold mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-5xl font-light mb-4">Welcome to Premium</h1>
            <p className="text-text-muted mb-8">Every door is open now. See who admires you and go explore.</p>
            <Link to="/likes" className="inline-block px-10 py-4 bg-gold text-ink rounded-full text-sm uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors">See your admirers</Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="payment-error">
            <XCircle size={48} className="mx-auto text-red-400 mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl font-light mb-4">We couldn't confirm payment</h1>
            <Link to="/premium" className="inline-block px-10 py-4 bg-wine rounded-full text-sm uppercase tracking-[0.2em]">Try again</Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="pt-32 min-h-screen text-center px-6">
      <div data-testid="payment-cancel" className="max-w-md mx-auto">
        <XCircle size={48} className="mx-auto text-text-muted mb-6" strokeWidth={1.5} />
        <h1 className="font-display text-5xl font-light mb-4">Checkout cancelled</h1>
        <p className="text-text-muted mb-8">No charge was made. You can upgrade whenever you're ready.</p>
        <Link to="/premium" className="inline-block px-10 py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em]">Back to Premium</Link>
      </div>
    </div>
  );
}
